import type { Filter, SortKey } from 'common/types/summaryDefinitions';

export interface DashboardFieldOption {
  // Set when the option is offered but cannot be chosen for this slot, e.g. a per-share price on a
  // totals card. The string is the user-facing reason and is rendered as a second line on the row,
  // never as a tooltip: PrimaryNavigationMenuItem wraps rows in OverflowTooltip, which only opens
  // when the primary text overflows.
  //
  // One field rather than `disabled: boolean` + `reason?: string`, because that pair can be set
  // inconsistently and "disabled with no explanation" is the state this is meant to prevent.
  disabledReason?: string;
  // Category label for the group heading. Options arrive already sorted by group, then by whether
  // they are selectable, then by label.
  group?: string;
  // Set when the option is only in the list because the stored definition already names it — an
  // incompatible metric, or one hidden by the firm's access. It stays selectable: pruneDraftForSave
  // saves the value either way, so dropping it from the picker would make the control read "Not
  // set" over data that is still stored.
  isLegacy?: boolean;
  label: string;
  value: string;
}

interface DashboardFieldBase {
  // Stable and path-like ('display.title', 'fields.metrics', 'position.w'). Doubles as the React key
  // and the test-id suffix, so it deliberately is not the human label: labels change with copy, and
  // a label-keyed test id breaks on every wording tweak.
  key: string;
  label: string;
  helperText?: string;
}

interface DashboardTextField extends DashboardFieldBase {
  kind: 'text' | 'textarea';
  onChange: (next: string) => void;
  // '' means the key is absent.
  value: string;
}

interface DashboardNumberField extends DashboardFieldBase {
  kind: 'number';
  // Whether clearing the box is a real edit. An optional key (`fields.limit`) is removed when it is
  // emptied; a required one (`position.w`, `position.h`, `layout.columns`) keeps its last value,
  // because the draft has no valid representation of "no width" — the re-flow writes one back.
  isRequired?: boolean;
  max?: number;
  min?: number;
  onChange: (next: number | undefined) => void;
  value: number | undefined;
}

interface DashboardSelectField extends DashboardFieldBase {
  // `undefined` renders the leading blank entry. For an optional key that entry reads "Not set" and
  // picking it removes the key; for a required one it reads "Select…" and is a validation error.
  kind: 'select';
  isRequired?: boolean;
  onChange: (next: string | undefined) => void;
  options: readonly DashboardFieldOption[];
  // Large vocabularies get a searchable menu; small enums get a plain one.
  searchable?: boolean;
  value: string | undefined;
}

interface DashboardBooleanField extends DashboardFieldBase {
  // Tri-state on purpose. accent_bar / show_legend / show_trend_icon are the one styling rule the
  // backend errors on rather than coercing, so "unset" has to stay unset instead of being written
  // as `false` — which would be a behaviour change the user never asked for.
  kind: 'boolean';
  onChange: (next: boolean | undefined) => void;
  value: boolean | undefined;
}

interface DashboardOrderedListField extends DashboardFieldBase {
  // Ordered selection from a vocabulary: metrics, series, and a table's columns. Order is meaningful
  // in all three, which is why this is not a plain multi-select.
  kind: 'orderedList';
  addLabel: string;
  minItems?: number;
  onChange: (next: string[]) => void;
  options: readonly DashboardFieldOption[];
  value: readonly string[];
}

interface DashboardMultiSelectField extends DashboardFieldBase {
  kind: 'multiSelect';
  onChange: (next: string[]) => void;
  options: readonly DashboardFieldOption[];
  value: readonly string[];
}

interface DashboardStringListField extends DashboardFieldBase {
  // Free text entries: display tags.
  kind: 'stringList';
  maxItems?: number;
  onChange: (next: string[]) => void;
  placeholder?: string;
  value: readonly string[];
}

interface DashboardColorListField extends DashboardFieldBase {
  // styling.colors — the palette a chart draws from. Entries are positional (series n takes
  // colors[n]) and duplicates are legitimate, so unlike stringList these rows are keyed by index
  // rather than by value.
  kind: 'colorList';
  onChange: (next: string[]) => void;
  value: readonly string[];
}

interface DashboardSortKeysField extends DashboardFieldBase {
  kind: 'sortKeys';
  onChange: (next: SortKey[]) => void;
  options: readonly DashboardFieldOption[];
  value: readonly SortKey[];
}

interface DashboardFiltersField extends DashboardFieldBase {
  kind: 'filters';
  onChange: (next: Filter[]) => void;
  options: readonly DashboardFieldOption[];
  value: readonly Filter[];
}

interface DashboardReadOnlyField extends DashboardFieldBase {
  // Component type, component id, schema version, and the stored prompt.
  kind: 'readOnly';
  value: string;
}

export type DashboardEditField =
  | DashboardTextField
  | DashboardNumberField
  | DashboardSelectField
  | DashboardBooleanField
  | DashboardOrderedListField
  | DashboardMultiSelectField
  | DashboardStringListField
  | DashboardColorListField
  | DashboardSortKeysField
  | DashboardFiltersField
  | DashboardReadOnlyField;

export interface DashboardEditSection {
  fields: DashboardEditField[];
  title: string;
}

export interface DashboardIssues {
  // Keyed by index into the draft's components — the same index the backend prefixes its messages
  // with (`components[3]: …`), so client-side and server-side problems land in one bucket per tab.
  byComponentIndex: Map<number, string[]>;
  // Everything not attributable to a component: name, layout.columns, version, or a 403 detail.
  general: string[];
}

export const emptyDashboardIssues = (): DashboardIssues => ({
  byComponentIndex: new Map(),
  general: [],
});

export const hasDashboardIssues = (issues: DashboardIssues | null): boolean =>
  Boolean(issues && (issues.general.length > 0 || issues.byComponentIndex.size > 0));
